import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import {
  DENIAL_MAX_MB,
  MEDICAL_RECORD_MAX_MB,
  MEDICAL_RECORD_MAX_PAGES,
} from "@/lib/trustCopy";

// The FAQ copy lives apart from the route component so the prerender can
// read it to build FAQPage structured data without importing a component
// module. Answers are ReactNode because several embed links; the schema
// builder keeps only the plain-string ones.

export interface QA {
  q: string;
  a: ReactNode;
}

export function faqs(paidPriceLabel: string): QA[] {
  return [
    {
      q: "Do I need an account?",
      a: "No. Revetra is account-free by design. Your session lives in your browser tab — there is no profile, no login, and nothing tied to an email until you ask us to follow up on an appeal outcome.",
    },
    {
      q: "Is my data really purged?",
      a: "Yes — on a fixed schedule, not on a click. Your uploaded denial letter and medical records, the drafted appeal, and the chat transcript are automatically deleted within 24 hours. “Start over,” “Discard,” and closing the tab all clear the session from your browser right away; anything on our servers is removed by that same 24-hour retention sweep. This keeps your paid download/recovery link valid for the full window, so a stray click can never destroy a letter you paid for. We never retain your medical record or denial letter beyond that window.",
    },
    {
      q: "Do you use my information to train AI?",
      a: "Your documents, never. They are processed only to draft your appeal, and the AI providers we rely on are contractually barred from training on, retaining, or reusing your content. The one thing we do learn from is anonymized outcome data — whether an appeal succeeded, if you choose to tell us — which contains no documents and no identifying details.",
    },
    {
      q: "I paid but lost the email with my download link — what now?",
      a: (
        <>
          First, check your spam / junk folder — delivery emails sometimes land there. If it still
          hasn't arrived, go to{" "}
          <Link to="/restore" className="font-medium text-ink underline">
            Restore your appeal
          </Link>{" "}
          and enter the email you used at checkout; we'll re-send your secure link. Download links
          stay valid for 24 hours after delivery. If nothing arrives after a few minutes, email
          support and we'll help.
        </>
      ),
    },
    {
      q: "What does the free tier include?",
      a: "OCR of your denial letter, extraction of the carrier, claim number, denial reason, and cited policies, and an overturn-probability estimate. The free tier ends at the appeal-letter step.",
    },
    {
      q: `What does the ${paidPriceLabel} unlock?`,
      a: "Everything: the full appeal letter drafted section-by-section against your plan documents, citation verification on every paragraph, medical-record analysis (dates of service, vitals, lab values, treatment history), refinement with diff / accept / revert, and PDF + Word export. One flat fee per appeal — no subscription, no upsells.",
    },
    {
      q: "What if I cannot afford it?",
      a: (
        <>
          Please contact us at{" "}
          <a className="font-medium text-ink underline" href="mailto:support@revetra.net">
            support@revetra.net
          </a>
          .
        </>
      ),
    },
    {
      q: "Can the assistant guarantee my appeal will succeed?",
      a: "No. Roughly half of appealed denials are overturned across U.S. carriers, but every case is different. Revetra gives you a credible, evidence-cited draft — not a guarantee.",
    },
    {
      q: "Is this legal advice?",
      a: "No. Revetra is a drafting tool, not a law firm. For complex denials — especially involving ERISA or external review — consult a patient advocate or attorney.",
    },
    {
      q: "How do you keep my information secure?",
      a: "Everything you upload is encrypted in transit and at rest, access is limited to the minimum needed to run the service, and your documents and their extracted data are automatically deleted within 24 hours. No system is perfectly secure, but if an incident ever affected your data, we would notify you as required by law.",
    },
    {
      q: "What are my privacy rights?",
      a: "You can ask us to delete your data early through the help link in your session, and we never sell or rent your information. Depending on where you live — for example, California or Washington — you may have additional rights over your health data, such as the right to access or delete it; contact us through the help center to exercise them.",
    },
    {
      q: "What file types do you accept?",
      a: `For the denial: a PDF or a photo (JPG / PNG) up to ${DENIAL_MAX_MB} MB — a clear phone picture of the letter works. The medical record is PDF only, capped at ${MEDICAL_RECORD_MAX_MB} MB and ${MEDICAL_RECORD_MAX_PAGES} pages — upload the key sections (H&P, discharge summary, relevant consults), not a full chart export. DOCX support is on the roadmap.`,
    },
    {
      q: "What if I can't get my medical record?",
      a: (
        <>
          You can still get an appeal. Without your medical record, Revetra builds a{" "}
          <span className="font-medium text-ink">coverage-policy letter</span> that argues from your
          denial and your plan's own coverage rules — effective for coverage and authorization
          denials. Be aware your odds are lower than with the chart, especially when the denial
          turns on clinical detail (medical necessity, level of care, length of stay).
          <br />
          <br />
          We recommend requesting your records first. Contact your provider's Health Information
          Management (medical records) department — it's usually free and turns around in a few days
          — or check your health system's patient app / online portal, which often lets you download
          visit notes, lab results, and discharge summaries yourself. You can add your record any
          time before you download, and Revetra will upgrade the letter to the full, chart-grounded
          version.
        </>
      ),
    },
  ];
}
